import React, { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import type { Task } from '@/types';

import styles from '../QuickAddModal.module.css';

interface QuickAddContentProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (block: NewBlockData) => void;
  initialStartTime?: number;
  initialDate?: string; // YYYY-MM-DD
  schedulingTask?: Task | null;
}

export interface NewBlockData {
  title: string;
  date?: string; // YYYY-MM-DD, defaults to selected date if omitted
  startTime: number;
  duration: number;
  categoryId: string;
  color: string;
  notes: string;
  linkedTaskId?: string;
}

const DURATION_OPTIONS = [
  { value: 15, label: '15m' },
  { value: 30, label: '30m' },
  { value: 45, label: '45m' },
  { value: 60, label: '1h' },
  { value: 120, label: '2h' },
];

const CATEGORIES = [
  { id: 'deep-work', name: 'Deep Work', color: '#2b6cb0' },
  { id: 'shallow', name: 'Shallow', color: '#9f7aea' },
  { id: 'personal', name: 'Personal', color: '#38a169' },
  { id: 'meeting', name: 'Meeting', color: '#ed8936' },
];

const COLORS = ['#2b6cb0', '#9f7aea', '#38a169', '#ed8936', '#e53e3e', '#718096'];

// Convert minutes to 24-hour format for input type="time"
function minutesToTimeInput(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

// Convert time input string to minutes
function timeInputToMinutes(timeStr: string): number {
  const [hours, mins] = timeStr.split(':').map(Number);
  return hours * 60 + mins;
}

function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function getCurrentTimeMinutes() {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

export function QuickAddContent({
  isOpen,
  onClose,
  onSave,
  initialStartTime,
  initialDate,
  schedulingTask,
}: QuickAddContentProps) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(initialDate || format(new Date(), 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState(initialStartTime ?? getCurrentTimeMinutes());
  const [duration, setDuration] = useState(30);
  const [categoryId, setCategoryId] = useState('deep-work');
  const [color, setColor] = useState('#2b6cb0');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Pre-fill from task if scheduling a task
      if (schedulingTask) {
        setTitle(schedulingTask.title);
        setDate(initialDate || format(new Date(), 'yyyy-MM-dd'));
        setStartTime(initialStartTime ?? getCurrentTimeMinutes());
        setDuration(schedulingTask.estimatedDuration || 30);
        setCategoryId(schedulingTask.categoryId || 'deep-work');
        const category = CATEGORIES.find((c) => c.id === schedulingTask.categoryId);
        setColor(category?.color || '#2b6cb0');
        setNotes(schedulingTask.notes || '');
      } else {
        setTitle('');
        setDate(initialDate || format(new Date(), 'yyyy-MM-dd'));
        setStartTime(initialStartTime ?? getCurrentTimeMinutes());
        setDuration(30);
        setCategoryId('deep-work');
        setColor('#2b6cb0');
        setNotes('');
      }
    }
  }, [isOpen, initialStartTime, schedulingTask]);

  useEffect(() => {
    const category = CATEGORIES.find((c) => c.id === categoryId);
    if (category) {
      setColor(category.color);
    }
  }, [categoryId]);

  const handleSave = useCallback(() => {
    if (!title.trim()) return;

    onSave({
      title: title.trim(),
      date,
      startTime,
      duration,
      categoryId,
      color,
      notes: notes.trim(),
      linkedTaskId: schedulingTask?.id,
    });

    onClose();
  }, [title, date, startTime, duration, categoryId, color, notes, schedulingTask, onSave, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && e.metaKey) {
        handleSave();
      }
    },
    [onClose, handleSave]
  );

  return (
    <div className={`${styles.modal} ${styles.panelModal}`} onKeyDown={handleKeyDown}>
      <div className={styles.header}>
        <h2 id="quick-add-title" className={styles.title}>
          {schedulingTask ? 'Schedule Task' : 'New Block'}
        </h2>
      </div>

      <div className={styles.body}>
        <div className={styles.formGroup}>
          <label htmlFor="block-title" className={styles.label}>
            Title
          </label>
          <input
            id="block-title"
            type="text"
            className={styles.input}
            placeholder="What are you working on?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="block-date" className={styles.label}>
            Date
          </label>
          <input
            id="block-date"
            type="date"
            className={styles.input}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Start Time</label>
          <div className={styles.timeControls}>
            <button
              className={styles.timeBtn}
              onClick={() => setStartTime((prev) => (prev - 15 + 1440) % 1440)}
              title="−15 min"
            >
              −
            </button>
            <input
              type="time"
              className={styles.timeInput}
              value={minutesToTimeInput(startTime)}
              onChange={(e) => setStartTime(timeInputToMinutes(e.target.value))}
            />
            <button
              className={styles.timeBtn}
              onClick={() => setStartTime((prev) => (prev + 15) % 1440)}
              title="+15 min"
            >
              +
            </button>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>End Time</label>
          <div className={styles.timeControls}>
            <button
              className={styles.timeBtn}
              onClick={() => setDuration((prev) => Math.max(15, prev - 15))}
              title="−15 min"
            >
              −
            </button>
            <input
              type="time"
              className={styles.timeInput}
              value={minutesToTimeInput(startTime + duration)}
              onChange={(e) => {
                const endMinutes = timeInputToMinutes(e.target.value);
                const newDuration = endMinutes - startTime;
                if (newDuration > 0) {
                  setDuration(newDuration);
                }
              }}
            />
            <button
              className={styles.timeBtn}
              onClick={() => setDuration((prev) => Math.min(480, prev + 15))}
              title="+15 min"
            >
              +
            </button>
            <span className={styles.timeDuration}>({formatDuration(duration)})</span>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Quick Duration</label>
          <div className={styles.chipGroup}>
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.chip} ${duration === opt.value ? styles.active : ''}`}
                onClick={() => setDuration(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Category</label>
          <div className={styles.chipGroup}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`${styles.chip} ${styles.categoryChip} ${
                  categoryId === cat.id ? styles.active : ''
                }`}
                style={
                  categoryId === cat.id
                    ? {
                        borderColor: cat.color,
                        background: `${cat.color}20`,
                        color: cat.color,
                      }
                    : {}
                }
                onClick={() => setCategoryId(cat.id)}
              >
                <span
                  className={styles.categoryDot}
                  style={{ background: cat.color }}
                  aria-hidden="true"
                />
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Color</label>
          <div className={styles.colorPicker}>
            {COLORS.map((c) => (
              <button
                key={c}
                className={`${styles.colorSwatch} ${color === c ? styles.active : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
                aria-label={`Select color ${c}`}
              >
                {color === c && <span aria-hidden="true">✓</span>}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="block-notes" className={styles.label}>
            Notes (optional)
          </label>
          <textarea
            id="block-notes"
            className={styles.textarea}
            placeholder="Add details, links, context..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.cancelBtn} onClick={onClose}>
          Cancel
        </button>
        <button className={styles.saveBtn} onClick={handleSave} disabled={!title.trim()}>
          Save Block
        </button>
      </div>
    </div>
  );
}
